import dayjs from 'dayjs';
import LessonCard from './LessonCard/LessonCard';
import { Accordion, Badge, Blockquote, Box, Center, Heading, Spacer, Text } from '@chakra-ui/react';
import { formatMonth, formatWeekday } from '@opanuj/shared/utils';
import { LessonStatus, Weekday } from '@opanuj/shared/types';
import { LessonWithClient } from './LessonsController';
import { useSettings } from '../../../hooks/useSettings';

const SEASON_MONTHS_ORDER = [8, 9, 10, 11, 0, 1, 2, 3, 4, 5, 6, 7]

export type LessonsListProps = {
	lessons: LessonWithClient[];
	selectedLessonId?: string | null;
	onSelectLesson?: (lessonId: string) => void;
};

const WEEKDAYS: Weekday[] = [
	Weekday.Sun,
	Weekday.Mon,
	Weekday.Tue,
	Weekday.Wed,
	Weekday.Thu,
	Weekday.Fri,
	Weekday.Sat,
];

const getWeekStart = (date: dayjs.Dayjs) =>
	date.startOf('day').subtract((date.day() + 6) % 7, 'day');

const getIsoWeekNumber = (date: dayjs.Dayjs) => {
	const mondayIndex = (date.day() + 6) % 7;
	const thursday = date.startOf('day').add(3 - mondayIndex, 'day');
	const firstWeekStart = getWeekStart(dayjs(`${thursday.year()}-01-04`));

	return Math.floor(thursday.diff(firstWeekStart, 'day') / 7) + 1;
};

function groupLessonsByWeek(lessons: LessonWithClient[], specialDayKeys: string[]) {
	const weeks = new Map<string, Map<string, LessonWithClient[]>>();

	lessons.forEach((lesson) => {
		const date = dayjs(lesson.timestamp * 1000);
		const weekStart = getWeekStart(date);
		const weekKey = weekStart.format('YYYY-MM-DD');
		const dayKey = date.format('YYYY-MM-DD');
		const days = weeks.get(weekKey) ?? new Map<string, LessonWithClient[]>();
		const dayLessons = days.get(dayKey) ?? [];

		dayLessons.push(lesson);
		days.set(dayKey, dayLessons);
		weeks.set(weekKey, days);
	});

	specialDayKeys.forEach((dayKey) => {
		const date = dayjs(dayKey);
		const weekKey = getWeekStart(date).format('YYYY-MM-DD');
		const days = weeks.get(weekKey) ?? new Map<string, LessonWithClient[]>();

		if (!days.has(dayKey)) days.set(dayKey, []);
		weeks.set(weekKey, days);
	});

	return [...weeks.entries()].sort(([first], [second]) => first.localeCompare(second));
}

export default function LessonsList({ lessons, selectedLessonId, onSelectLesson }: LessonsListProps) {
	const [settings] = useSettings();
	const daysOff = settings?.daysOff ?? [];
	const currentDayKey = dayjs().format('YYYY-MM-DD');
	const specialDayKeys = [...new Set([...daysOff, currentDayKey])];

	if (!lessons) {
		return (
			<Center>
				<Heading m={3}>Brak wystartowanych lekcji</Heading>
			</Center>
		);
	}
	const currentWeekStart = getWeekStart(dayjs());
	const upcomingWeekStart = currentWeekStart.add(7, 'day');
	const currentMonth = dayjs().month();
	const defaultExpandedWeekKeys = [
		currentWeekStart.format('YYYY-MM-DD'),
		upcomingWeekStart.format('YYYY-MM-DD'),
	];
	const defaultExpandedMonths = [...new Set([
		currentWeekStart.month(),
		upcomingWeekStart.month(),
	])]
		.filter((month) => SEASON_MONTHS_ORDER.includes(month))
		.map(String);
	const lessonsByMonth = lessons.reduce((acc, l) => {
		const idx = dayjs(l.timestamp * 1000).month()
		acc[idx].lessons.push(l)
		if (l.status === LessonStatus.Cancelled) {
			acc[idx].cancelledCount++
			if (l.rescheduleOptions)
				acc[idx].awaitingReschedule++
		}
		acc[idx].periodEnd = dayjs(l.timestamp * 1000).endOf('month');
		acc[idx].periodStart = dayjs(l.timestamp * 1000).startOf('month');

		return acc
	}
		, SEASON_MONTHS_ORDER.reduce((a, i) => ({ ...a, [i]: { lessons: [], cancelledCount: 0, awaitingReschedule: 0, periodStart: dayjs(), periodEnd: dayjs() } }), {} as { [idx: number]: { lessons: LessonWithClient[], cancelledCount: number, awaitingReschedule: number, periodStart: dayjs.Dayjs, periodEnd: dayjs.Dayjs } }));

	return (
		<Accordion.Root lazyMount unmountOnExit defaultValue={defaultExpandedMonths} multiple collapsible px={4}>
			{SEASON_MONTHS_ORDER.map((monthKey) => {
				const monthLessons = lessonsByMonth[monthKey];
				const monthSpecialDayKeys = specialDayKeys.filter((dayKey) => dayjs(dayKey).month() === monthKey);
				monthLessons.lessons.sort(
					(l1, l2) => l1.timestamp - l2.timestamp
				);
				if (!monthLessons.lessons.length && !monthSpecialDayKeys.length) {
					return null;
				} else {
					return (
						<Accordion.Item key={monthKey} value={String(monthKey)}>
							<h2>
								<Accordion.ItemTrigger _hover={{ bg: 'gray.50' }} cursor='pointer'>
									<Box as="span" flex="1" textAlign="left" textTransform='uppercase' letterSpacing='wider' color={monthKey === currentMonth ? 'orange.500' : 'gray.600'} fontSize="sm">
										{formatMonth(monthKey)}
									</Box>
									<Spacer />
									{monthLessons.cancelledCount ? (
										<Badge colorPalette='orange'>{`Odwołanych: ${monthLessons.cancelledCount}`}</Badge>
									) : null}
									{monthLessons.awaitingReschedule ? (
										<Badge colorPalette='yellow'>{`Propozycji terminów: ${monthLessons.awaitingReschedule}`}</Badge>
									) : null}
									<Accordion.ItemIndicator />
								</Accordion.ItemTrigger>
							</h2>
							<Accordion.ItemContent pb={4}><Accordion.ItemBody px={2}>
								{(() => {
									const weeks = groupLessonsByWeek(monthLessons.lessons, monthSpecialDayKeys);

									return (
										<Accordion.Root variant="plain" lazyMount unmountOnExit defaultValue={weeks
											.filter(([weekKey]) => defaultExpandedWeekKeys.includes(weekKey))
											.map(([weekKey]) => weekKey)} multiple collapsible>
											{weeks.map(([weekKey, days]) => {
												const weekStart = dayjs(weekKey);
												const weekEnd = weekStart.add(6, 'day');

												return (
													<Accordion.Item key={weekKey} value={weekKey}>
														<h3>
															<Accordion.ItemTrigger px={2} _hover={{ bg: 'gray.100' }} cursor='pointer'>
																<Text fontWeight="bold" flex="1" textAlign="left" color={weekKey === currentWeekStart.format('YYYY-MM-DD') ? 'orange.500' : undefined}>
																	{`${weekStart.format('DD.MM')} - ${weekEnd.format('DD.MM')}`}
																</Text>
																<Text fontWeight="bold" flex="1" textAlign="right" color={weekKey === currentWeekStart.format('YYYY-MM-DD') ? 'orange.500' : undefined}>
																	{`tydzień #${getIsoWeekNumber(weekStart)}`}
																</Text>
																<Accordion.ItemIndicator />
															</Accordion.ItemTrigger>
														</h3>
														<Accordion.ItemContent><Accordion.ItemBody ml={8}>
															{[...days.entries()].sort(([first], [second]) => first.localeCompare(second)).map(([dayKey, dayLessons]) => {
																const isCurrentDay = dayKey === currentDayKey;
																const isDayOff = daysOff.includes(dayKey);

																return (
																	<Box key={dayKey} mb={3} bg={isDayOff ? 'gray.100' : undefined} borderRadius={isDayOff ? 'md' : undefined} p={isDayOff ? 2 : undefined}>
																		<Text fontWeight="bold" textTransform="capitalize" color={isCurrentDay ? 'orange.500' : 'gray.600'} mt={2} mb={1}>
																			{`${formatWeekday(WEEKDAYS[dayjs(dayKey).day()])}, ${dayjs(dayKey).format('DD.MM.YYYY')}`}
																		</Text>
																		{isDayOff && <Text color="gray.500" fontSize="sm" fontStyle="italic">Dzień wolny</Text>}
																		<Blockquote.Root borderColor={isCurrentDay ? 'orange.500' : undefined} borderStartWidth={2}>
																			{dayLessons.sort((first, second) => first.timestamp - second.timestamp).map((lesson) => (
																				<LessonCard key={lesson.id} lesson={lesson} selected={lesson.id === selectedLessonId} onSelect={onSelectLesson} />
																			))}
																		</Blockquote.Root>
																	</Box>
																);
															})}
														</Accordion.ItemBody></Accordion.ItemContent>
													</Accordion.Item>
												);
											})}
										</Accordion.Root>
									);
								})()}
							</Accordion.ItemBody></Accordion.ItemContent>
						</Accordion.Item>
					);
				}
			})}
		</Accordion.Root>
	);
}
